import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import AffiliationService from "../../services/AffiliationService";
import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import AffiliationCard from "../../components/Affiliation/AfiliationCard";
import MainButton from "../../components/Elements/MainButton/MainButton";
import DataTable from "../../components/Elements/DataTable/DataTable";
import MainInput from "../../components/Elements/MainInput/MainInput";

import "./Affiliation.css";

const ns = "views/Affiliation";

const Affiliation = () => {
  const { t } = useTranslation();
  const { walletCurrency = "EUR", userId } = useContext(AuthContext);
  const [affiliateData, setAffiliateData] = useState(null);
  const [filters, setFilters] = useState({
    date_start_inclusive: "",
    date_end_exclusive: "",
  });
  const [created, setCreated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  const columns = [
    { key: "email_private", label: t("affiliation.email") },
    { key: "total_amount", label: t("affiliation.totalAmount") },
    { key: "provision", label: t("affiliation.provision") },
    { key: "currency", label: t("affiliation.currency") },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const affiliateRes = await AffiliationService.getAffiliation();
        setAffiliateData(affiliateRes);
        setCreated(!!affiliateRes.wallet_id);
      } catch (err) {
        if (err.response?.status === 404) {
          setCreated(false);
          setAffiliateData(null);
        } else {
          setError(t("errors.failedToLoadAffiliateData", "Failed to load affiliate data"));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  const handleCreateAffiliation = async () => {
    if (creating) return;

    const confirmed = window.confirm("Are you sure you want to create an Affiliation account?\n\n");

    if (!confirmed) return;

    try {
      setCreating(true);
      await AffiliationService.createAffiliation();

      // Refresh data po utworzeniu
      const affiliateRes = await AffiliationService.getAffiliation();
      setAffiliateData(affiliateRes);
      setCreated(true);
    } catch (err) {
      setError(t("errors.failedToCreateAffiliation", "Failed to create affiliation"));
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {}, [filters]);

  const fetchAffiliateStats = async ({ token, skip, limit, sort_by, sort_order, filters: _ }) => {
    const params = new URLSearchParams();
    if (skip) params.append("skip", skip);
    if (limit) params.append("limit", limit);
    if (sort_by) params.append("sort_by", sort_by);
    if (sort_order) params.append("sort_order", sort_order);

    if (filters.email) params.append("email", filters.email);
    if (filters.date_start_inclusive) params.append("date_start_inclusive", filters.date_start_inclusive);
    if (filters.date_end_exclusive) params.append("date_end_exclusive", filters.date_end_exclusive);

    const data = await AffiliationService.getAffiliationStats(filters.date_start_inclusive, filters.date_end_exclusive);

    return {
      items: data.filtered?.users || [],
      total: data.total.total_users_registered,
      filtered: data.filtered?.users_registered,
    };
  };

  const createButton = !created && !loading && (
    <MainButton className="btn btn-primary" onClick={handleCreateAffiliation} disabled={creating}>
      {creating ? t("affiliation.creating") : t("affiliation.createAffiliation")}
    </MainButton>
  );

  if (loading) {
    return (
      <div className="affiliation-page">
        <div>{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="affiliation-page">
      <PageHeader
        title={t("affiliation.title")}
        description={t("affiliation.subtitle")}
        actions={createButton}
      />

      {error && <div className="error-message">{error}</div>}

      <div className="affiliation-container">
        <AffiliationCard key={created ? "created" : "not-created"} />
      </div>

      {created && (
        <>
          <div className="affiliation-date-filters">
            <div className="affiliation-date-filters__row">
              <div className="affiliation-date-filters__field">
                <label className="dt-label">{t("affiliation.fromDate")}</label>
                <MainInput
                  type="date"
                  value={filters.date_start_inclusive || ""}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      date_start_inclusive: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="affiliation-date-filters__field">
                <label className="dt-label">{t("affiliation.toDate")}</label>
                <MainInput
                  type="date"
                  value={filters.date_end_exclusive || ""}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      date_end_exclusive: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>

          <div className="affiliation-stats-table">
            <DataTable
              fetchDataFunction={fetchAffiliateStats}
              columns={columns}
              showSort={false}
              defaultSortBy="email_private"
              itemKey="email_private"
              showFilters={false}
              showPageSize={false}
              showCheckboxes={false}
              tableId="affiliation-stats-table"
              emptyMessage="No affiliated users found"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Affiliation;
